import { loadStripe } from '@stripe/stripe-js';
import { API_ENDPOINTS, apiCall, isApiConfigured } from '../config/api';

// Stripe Price IDs for different plans and billing cycles
const STRIPE_PRICES = {
  Solo: {
    monthly: 'price_1Red3HHIQM8eD2nDf4F5PKpk',
    annual: 'price_1Red3HHIQM8eD2nD1lEwmr3j'
  },
  Studio: {
    monthly: 'price_1RhdkZHIQM8eD2nDOC03JaLn',
    annual: 'price_1RhdkZHIQM8eD2nD17mudPWR'
  },
  Firm: {
    monthly: 'price_1RhdoIHIQM8eD2nDEb8mQ3iI', 
    annual: 'price_1RhdoIHIQM8eD2nDEb8mQ3iI'   
  }
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const EXEMPT_EMAILS = ['pr@oquli.ca', 'dd@oquli.ca', 'info@oquli.ca', 'uppeshivani2001@gmail.com'];


export const checkSubscriptionStatus = async (userEmail) => {
  if (!userEmail) {
    console.warn('No email provided for subscription check');
    return { isSubscribed: false, isExempt: false };
  }

  const normalizedEmail = userEmail.toLowerCase().trim();
  if (process.env.NODE_ENV === 'development') {
    console.log('Checking subscription for normalized email:', normalizedEmail ? `${normalizedEmail.substring(0, 3)}***` : null);
  }
  
  const isExempt = EXEMPT_EMAILS.some(email => email.toLowerCase().trim() === normalizedEmail);
  if (process.env.NODE_ENV === 'development') {
    console.log('Is user exempt?', isExempt, 'Checked against:', EXEMPT_EMAILS.length, 'exempt emails');
  }

  if (isExempt) {
    if (process.env.NODE_ENV === 'development') {
      console.log('User is exempt, granting full access');
    }
    return {
      isSubscribed: true,
      isExempt: true,
      subscriptionData: {
        email: normalizedEmail,
        plan: 'Firm',
        status: 'active',
        nextBillingDate: null
      }
    };
  }

  console.log('Development mode: granting access to all users');
  return {
    isSubscribed: true,
    isExempt: false,
    subscriptionData: {
      email: normalizedEmail,
      plan: 'Professional',
      status: 'active',
      nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    }
  };
};

export const redirectToSubscriptionPage = async (userEmail, plan = 'Solo', billingCycle = 'monthly') => {
  try {
    const stripe = await stripePromise;
    const priceId = STRIPE_PRICES[plan]?.[billingCycle];
    
    if (!priceId) {
      throw new Error(`Price ID not found for plan: ${plan} (${billingCycle})`);
    }
    
    const { error } = await stripe.redirectToCheckout({
      mode: 'subscription',
      lineItems: [{ price: priceId, quantity: 1 }],
      successUrl: window.location.origin,
      cancelUrl: window.location.origin,
      customerEmail: userEmail
    });

    if (error) {
      throw new Error(error.message);
    }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

export const getSubscriptionDetails = async (userEmail) => {
  if (!userEmail) {
    console.warn('No email provided for subscription details');
    return null;
  }

  const normalizedEmail = userEmail.toLowerCase().trim();
  if (process.env.NODE_ENV === 'development') {
    console.log('Getting subscription details for normalized email:', normalizedEmail ? `${normalizedEmail.substring(0, 3)}***` : null);
  }
  
  const isExempt = EXEMPT_EMAILS.some(email => email.toLowerCase() === normalizedEmail);
  if (process.env.NODE_ENV === 'development') {
    console.log('Is user exempt?', isExempt);
  }

  if (isExempt) {
    if (process.env.NODE_ENV === 'development') {
      console.log('User is exempt, returning firm details');
    }
    return {
      isSubscribed: true,
      plan: 'Firm',
      status: 'active',
      nextBillingDate: null
    };
  }

  if (isApiConfigured()) {
    try {
      const data = await apiCall(`${API_ENDPOINTS.SUBSCRIPTION_DETAILS}?userEmail=${encodeURIComponent(normalizedEmail)}`);
      return {
        isSubscribed: true,
        plan: data.plan,
        status: data.status,
        nextBillingDate: data.nextBillingDate,
        billingCycle: data.billingCycle
      };
    } catch (error) {
      console.error('Error fetching subscription details from API:', error);
    }
  }

  console.log('Development mode: returning active subscription for all users');
  return {
    isSubscribed: true,
    plan: 'Professional',
    status: 'active',
    nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  };
};


export const getTeamUsers = async (userEmail) => {
  if (!userEmail) {
    console.warn('No email provided for team users');
    return { users: [], subscriptionOwnerEmail: null };
  }

  const normalizedEmail = userEmail.toLowerCase().trim();
  
  const isExempt = EXEMPT_EMAILS.some(email => email.toLowerCase().trim() === normalizedEmail);
  
  if (isExempt) {
    if (process.env.NODE_ENV === 'development') {
      console.log('User is exempt, returning empty team users list');
    }
    return { users: [], subscriptionOwnerEmail: normalizedEmail };
  }

  if (isApiConfigured()) {
    console.log('API is configured, making call to:', `${API_ENDPOINTS.TEAM_USERS}?userEmail=${encodeURIComponent(normalizedEmail)}`);
    try {
      const data = await apiCall(`${API_ENDPOINTS.TEAM_USERS}?userEmail=${encodeURIComponent(normalizedEmail)}`);
      console.log('API call successful, received data:', data);
      return {
        users: data.users || [],
        subscriptionOwnerEmail: data.subscriptionOwnerEmail || normalizedEmail
      };
    } catch (error) {
      console.error('Error fetching team users:', error);
    }
  }
  
  return { users: [], subscriptionOwnerEmail: null };
};


export const addTeamUser = async (userEmail, newUser) => {
  if (!isApiConfigured()) {
    throw new Error('API not configured for adding team users');
  }

  try {
    const data = await apiCall(API_ENDPOINTS.ADD_USER, {
      method: 'POST',
      body: JSON.stringify({
        userEmail: userEmail,
        name: newUser.name,
        email: newUser.email,
        role: newUser.role || 'standard' 
      }),
    });
    return data;
  } catch (error) {
    console.error('Error adding team user:', error);
    throw error;
  }
};

export const updateUserStatus = async (userEmail, userId, status) => {
  if (!isApiConfigured()) {
    throw new Error('API not configured for updating user status');
  }

  try {
    const data = await apiCall(API_ENDPOINTS.UPDATE_USER_STATUS, {
      method: 'PUT',
      body: JSON.stringify({
        userEmail: userEmail,
        userId: userId,
        status: status
      }),
    });
    return data;
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

export const updateUserRole = async (userEmail, userId, role) => {
  if (!isApiConfigured()) {
    throw new Error('API not configured for updating user roles');
  }

  try {
    const data = await apiCall(API_ENDPOINTS.UPDATE_USER_ROLE, {
      method: 'PUT',
      body: JSON.stringify({
        userEmail: userEmail,
        userId: userId,
        role: role
      }),
    });
    return data;
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

export const deleteTeamUser = async (userEmail, userId) => {
  if (!isApiConfigured()) {
    throw new Error('API not configured for deleting team users');
  }

  try {
    const data = await apiCall(API_ENDPOINTS.DELETE_USER, {
      method: 'DELETE',
      body: JSON.stringify({
        userEmail: userEmail,
        userId: userId
      }),
    });
    return data;
  } catch (error) {
    console.error('Error deleting team user:', error);
    throw error;
  }
};

export const getSubscriptionPlan = async (userEmail) => {
  if (!userEmail) {
    console.warn('No email provided for subscription plan');
    return 'Solo';
  }

  const normalizedEmail = userEmail.toLowerCase().trim();
  
  const isExempt = EXEMPT_EMAILS.some(email => email.toLowerCase().trim() === normalizedEmail);
  
  if (isExempt) {
    if (process.env.NODE_ENV === 'development') {
      console.log('User is exempt, returning Firm plan');
    }
    return 'Firm';
  }

  if (isApiConfigured()) {
    try {
      const data = await apiCall(`${API_ENDPOINTS.SUBSCRIPTION_DETAILS}?userEmail=${encodeURIComponent(normalizedEmail)}`);
      return data.plan || 'Solo';
    } catch (error) {
      console.error('Error fetching subscription plan:', error);
    }
  }
  
  const details = await getSubscriptionDetails(userEmail);
  return details?.plan || 'Solo';
};

export const calculatePlanLimits = (plan) => {
  switch (plan) {
    case 'Solo': return 5;
    case 'Studio': return 25;
    case 'Firm': return Infinity; 
    default: return 5;
  }
};

export const redirectToPlanUpgrade = async (userEmail, newPlan, billingCycle = 'monthly') => {
  try {
    const stripe = await stripePromise;
    const priceId = STRIPE_PRICES[newPlan]?.[billingCycle];
    
    if (!priceId) {
      throw new Error(`Price ID not found for plan: ${newPlan} (${billingCycle})`);
    }
    
    const { error } = await stripe.redirectToCheckout({
      mode: 'subscription',
      lineItems: [{ price: priceId, quantity: 1 }],
      successUrl: `${window.location.origin}?plan_upgrade=success`,
      cancelUrl: window.location.origin,
      customerEmail: userEmail
    });

    if (error) {
      throw new Error(error.message);
    }
  } catch (error) {
    console.error('Error redirecting to plan upgrade:', error);
    throw error;
  }
}; 